import { Injectable } from '@angular/core'
import { Booking, bookingsPath, Client, clientsPath, merchantsPath, Occupant, occupantsPath, Parking, parkingsPath } from '@parkupp/core'
import { addDoc, collection, collectionData, deleteDoc, doc, docData, Firestore, orderBy, query, serverTimestamp, where } from '@angular/fire/firestore'
import * as firestore from 'firebase/firestore'
import { AuthenticationService } from './authentication.service'
import { ParkingService } from './parking.service'
import { combineLatest, switchMap } from 'rxjs'
import { map } from 'rxjs/operators'

@Injectable({
    providedIn: 'root',
})
export class BookingService {
    constructor(private authSvc: AuthenticationService, public firestore: Firestore, private parkingService: ParkingService) {}

    createBooking(data: { clientKey: string; merchantKey: string; parkingKey: string; startDate?: string; occupantKeys?: string[]; bayNumber: string }) {
        return new Promise<any>((resolve) => {
            const booking = new Booking()
            booking.createdAt = serverTimestamp() as firestore.Timestamp
            booking.updatedAt = serverTimestamp() as firestore.Timestamp
            booking.clientRef = doc(this.firestore, clientsPath(), data.clientKey) as any
            booking.parkingRef = doc(this.firestore, parkingsPath(), data.parkingKey!) as any
            booking.merchantRef = doc(this.firestore, merchantsPath(), data.merchantKey!) as any
            booking.bayNumber = data.bayNumber

            if (data.startDate) {
                const startDateObject = new Date(data.startDate)
                booking.startDate = firestore.Timestamp.fromDate(startDateObject)
            }

            if (data.occupantKeys) {
                const occupantRefs: any[] = []
                data.occupantKeys.forEach((occupantKey: string) => {
                    occupantRefs.push(doc(this.firestore, occupantsPath(data.clientKey as string), occupantKey) as any)
                })

                booking.occupantRefs = occupantRefs
            }

            addDoc(collection(this.firestore, bookingsPath()), booking.parse()).then((docRef) => {
                // const dateString = moment(new Date(data.startDate)).format('YYYY-MM-DD')
                // const dateBayKey = dateString + '-bay-' + booking.bayNumber
                //
                // let bookings: any = {}
                // if (data.bookings) {
                //     bookings = data.bookings
                // }
                //
                // bookings[dateBayKey] = true
                resolve(true)
            })
        })
    }

    listBookings(merchantKey: string, permanentBooking = false) {
        let merchantRef = doc(this.firestore, merchantsPath(), merchantKey) as any
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1) // Set to yesterday's date

        let ref
        if (permanentBooking) {
            ref = query(collection(this.firestore, bookingsPath()), orderBy('startDate', 'desc'), where('merchantRef', '==', merchantRef), where('startDate', '==', null))
        } else {
            ref = query(
                collection(this.firestore, bookingsPath()),
                orderBy('startDate', 'desc'),
                where('merchantRef', '==', merchantRef),
                where('startDate', '>', yesterday) // Add this line to filter by startDate
            )
        }

        return collectionData(ref, { idField: 'documentId' }).pipe(
            switchMap((documents: any[]) => {
                return combineLatest(
                    documents.map((documentData) => {
                        let booking = new Booking(documentData)
                        booking.$key = documentData.documentId

                        const parkingObservable = docData(documentData.parkingRef).pipe(
                            map((parkingData) => {
                                booking.$parking = new Parking(parkingData)
                                booking.$parking.$key = documentData.parkingRef.id
                                return booking
                            })
                        )

                        const clientObservable = docData(documentData.clientRef).pipe(
                            switchMap((clientData) => {
                                booking.$client = new Client(clientData)
                                booking.$client.$key = documentData.clientRef.id

                                return collectionData(collection(this.firestore, occupantsPath(booking.$client.$key!)), { idField: 'documentId' }).pipe(
                                    map((occupantDocuments) => {
                                        const occupants = occupantDocuments
                                            .map((occupantData) => {
                                                let occupant = new Occupant(occupantData)
                                                occupant.$key = occupantData['documentId']
                                                return occupant
                                            })
                                            .filter((occupant) => !occupant.deletedAt)

                                        booking.$client!.$occupants = occupants
                                        return booking
                                    })
                                )
                            })
                        )

                        return combineLatest([parkingObservable, clientObservable]).pipe(
                            map(() => booking) // return the booking after both parking and client data are fetched
                        )
                    })
                )
            }),
            map((bookingsArrays) => bookingsArrays.flat()) // Flatten the array of arrays into a single array
        )
    }

    getBooking(bookingKey: string) {
        return new Promise<any>((resolve, reject) => {
            console.log(bookingKey)
            docData(doc(this.firestore, bookingsPath(), bookingKey)).subscribe((documentData: any) => {
                console.log(documentData)
                let booking = new Booking(documentData)
                booking.$key = bookingKey

                resolve(booking)
            })
        })
    }

    // listBookingsByUser(clientRef: any) {
    //     // let merchantRef = doc(this.firestore, merchantsPath(), merchantKey) as any
    //
    //     const today = new Date()
    //     today.setHours(0, 0, 0, 0) // Set to the start of today
    //
    //     const ref = query(
    //         collection(this.firestore, bookingsPath()),
    //         orderBy('createdAt'),
    //         where('clientRef', '==', clientRef),
    //         where('createdAt', '>=', today) // Add this line to filter for today and future dates
    //     )
    //
    //     return new Promise<Booking[]>((resolve, reject) => {
    //         const bookings: Booking[] = []
    //         collectionData(ref, { idField: 'documentId' }).subscribe((documents: any[]) => {
    //             documents.forEach((documentData) => {
    //                 let booking = new Booking(documentData)
    //                 booking.$key = documentData.documentId
    //
    //                 docData(documentData.parkingRef).subscribe((data) => {
    //                     booking.$parking = new Parking(data)
    //                     booking.$parking.$key = documentData.parkingRef.id
    //                 })
    //
    //                 bookings.push(booking)
    //             })
    //         })
    //         resolve(bookings)
    //     })
    // }

    async deleteBooking(bookingKey: string) {
        try {
            // Create a reference to the booking document
            const bookingRef = doc(this.firestore, bookingsPath(), bookingKey)

            // Delete the booking document
            await deleteDoc(bookingRef)

            return true
        } catch (error) {
            console.error(`Error deleting booking: ${error}`)
            throw error
        }
    }
}
